import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import MainLayout from "@/layouts/MainLayout";
import AuthService from "@/lib/auth";
import EmailAction from "@/components/questionnaire/EmailAction";
import ENV from "@/lib/env";

import { Mail, ChevronDown, ChevronUp, Users, MessageSquare } from "lucide-react";
import {
  calculateAge,
  formatDate,
  formatDOB,
  getGenderInitial,
  formatClinicPhone,
} from "@/lib/utils.ts";
import type { QuestionnaireResponseTypes } from "@/types/questionnaire";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { sendBulkSMS } from "@/utils/sendSMS";
import { toast } from "sonner";

// ── Helpers ──────────────────────────────────────────────────────────────────

const randomStringIsBad = (str: string): boolean => {
  const badWords = ["fkr", "mthfkr", "ngr", "nigger", "fck", "sht"];
  return badWords.some((word) => str.toLowerCase().includes(word));
};

const generateSMSEmbedUrlForPatient = async (patientId: string): Promise<string> => {
  const url = ENV.VITE_BASE_URL || window.location.origin;
  const maxRetries = 10;
  let attempts = 0;

  try {
    const smsLogs = await AuthService.getSMSLogsByPatient(patientId);
    const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
    const recentLogs = (smsLogs || []).filter((log) => {
      const t = new Date(log.updated_at || log.created_at).getTime();
      return !isNaN(t) && t >= oneDayAgo;
    });

    if (recentLogs.length >= 3) {
      console.error(`[SMS] Patient ${patientId} has already received ${recentLogs.length} SMS messages in the last 24 hours`);
      return "";
    }

    while (attempts < maxRetries) {
      const charset = "23456789abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ";
      const array = new Uint8Array(8);
      crypto.getRandomValues(array);
      const randomString = Array.from(array).map((b) => charset[b % charset.length]).join("");

      if (!randomStringIsBad(randomString) && !(await AuthService.checkSMSUniquePageId(randomString))) {
        return `${url}/q/${randomString}`;
      }
      attempts++;
    }
  } catch (error) {
    console.error("Error generating SMS URL:", error);
  }
  return "";
};

interface StaffUser {
  id: string;
  name: string;
  email: string;
  role?: string;
  created_at?: string;
  updated_at?: string;
}

type DoctorWithRole = StaffUser & { role_type: 'Primary Provider' | 'Secondary Provider' };

export default function QuestionnaireResponse() {
  const navigate = useNavigate();
  const [rawData, setRawData] = useState<QuestionnaireResponseTypes[]>([]);
  const [data, setData] = useState<QuestionnaireResponseTypes[]>([]);
  const [allDoctors, setAllDoctors] = useState<any[]>([]);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [selectedEmailResponses, setSelectedEmailResponses] = useState<QuestionnaireResponseTypes[]>([]);
  const [selectAllEmail, setSelectAllEmail] = useState(false);
  const [showBlue, setShowBlue] = useState(true);
  const [showRed, setShowRed] = useState(true);
  const [showAlertOnly, setShowAlertOnly] = useState(false);
  const [showPending, setShowPending] = useState(true);
  const [showCompleted, setShowCompleted] = useState(true);
  const [showReviewed, setShowReviewed] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [relevantDoctors, setRelevantDoctors] = useState<DoctorWithRole[]>([]);
  const [patientDoctorMap, setPatientDoctorMap] = useState<Map<string, DoctorWithRole[]>>(new Map());
  const [isFetchingDoctors, setIsFetchingDoctors] = useState(false);
  const [showPatientDetails, setShowPatientDetails] = useState(false);
  const [isDoctor, setIsDoctor] = useState(false);
  const [assignedPatientIds, setAssignedPatientIds] = useState<string[]>([]);

  const fetchDoctors = async () => {
    try {
      // Fetch all roles with is_allowed_to_add_notes=true (Doctor, NP, PA, CareCoordinator, etc.)
      const rolesMetadata = await AuthService.getRolesMetadata();
      const cpRoleNames = rolesMetadata
        .filter((r: any) => r.is_allowed_to_add_notes === true)
        .map((r: any) => r.name || r.role);

      const results = await Promise.all(
        cpRoleNames.map((role: string) => AuthService.getStaffByRole(role))
      );
      const allCpStaff = results.flat();
      // Deduplicate by id
      const unique = Array.from(new Map(allCpStaff.map((s: any) => [s.id, s])).values());
      const doctorsData = Array.isArray(unique) ? unique : [];
      setAllDoctors(doctorsData);
    } catch (e: any) {
      console.error("Error fetching doctors:", e);
      setAllDoctors([]);
    }
  };

  const fetchRelevantDoctors = async (selectedPatientIds: string[]) => {
    if (selectedPatientIds.length === 0) {
      setRelevantDoctors([]);
      setPatientDoctorMap(new Map());
      return;
    }
    try {
      setIsFetchingDoctors(true);
      console.log('🔍 Fetching relevant doctors for patients:', selectedPatientIds);
      const patientDoctorMapping = new Map<string, DoctorWithRole[]>();
      const uniqueDoctorsMap = new Map<string, DoctorWithRole>();

      for (const pid of selectedPatientIds) {
        console.log(`📡 Fetching encounters for patient ${pid}...`);
        const encounters = await AuthService.getPatientEncounters({ patient_id: pid });
        console.log(`📄 Encounters for ${pid}:`, encounters);

        // Find the primary provider from encounters OR patient record
        const patientData = await AuthService.getPatient(pid);
        const encounterWithDoctor = encounters.find((e: any) => e.primary_provider_id);
        const primaryProviderId = patientData?.primary_cp_uuid || encounterWithDoctor?.primary_provider_id;

        if (primaryProviderId) {
          console.log(`👨‍⚕️ Found primary_provider_id ${primaryProviderId} in encounter for patient ${pid}`);
          const doctorInfo = allDoctors.find(d =>
            String(d.id) === String(primaryProviderId) ||
            String(d.auth_uuid) === String(primaryProviderId)
          );

          if (doctorInfo) {
            console.log(`✅ Matched doctor info:`, doctorInfo);
            const doc: DoctorWithRole = {
              ...doctorInfo,
              role_type: 'Primary Provider'
            };
            patientDoctorMapping.set(pid, [doc]);
            uniqueDoctorsMap.set(doc.id, doc);
          } else {
            console.warn(`⚠️ Primary provider ${primaryProviderId} found in encounter but not in allDoctors list:`, allDoctors);
            patientDoctorMapping.set(pid, []);
          }
        } else {
          console.warn(`🛑 No primary_provider_id found in any encounter for patient ${pid}`);
          patientDoctorMapping.set(pid, []);
        }
      }

      console.log('🏁 Final patient-doctor mapping:', Object.fromEntries(patientDoctorMapping));
      setRelevantDoctors(Array.from(uniqueDoctorsMap.values()));
      setPatientDoctorMap(patientDoctorMapping);
    } catch (error) {
      console.error('Error fetching relevant doctors:', error);
      setRelevantDoctors([]);
      setPatientDoctorMap(new Map());
    } finally {
      setIsFetchingDoctors(false);
    }
  };

  const fetchData = async () => {
    try {
      // Filter logic matching SearchPatient.tsx
      const cognitoUser = await AuthService.getCurrentUser();
      const userId = cognitoUser?.username;

      if (userId) {
        const staffData = await AuthService.getProfile(userId);
        const docRole = staffData?.role?.toLowerCase() === "doctor";
        setIsDoctor(docRole);

        if (docRole && staffData?.id) {
          const encounters = await AuthService.getPatientEncounters({
            primary_provider_id: staffData.id,
          });
          const secondary = await AuthService.getPatientEncounters({
            secondary_provider_id: staffData.id,
          });
          let assignedPatients: any[] = [];
          try {
            const allPatients = await AuthService.getPatients();
            assignedPatients = (allPatients || []).filter(
              (p: any) => p.primary_cp_uuid === staffData.id || p.secondary_cp_uuid === staffData.id
            );
          } catch (e) {
            console.warn("Could not fetch assigned patients for doctor:", e);
          }
          const assignedIds = [
            ...new Set([
              ...encounters.map((e: any) => e.patient_id),
              ...secondary.map((e: any) => e.patient_id),
              ...assignedPatients.map((p: any) => p.id),
            ]),
          ];
          setAssignedPatientIds(assignedIds);
        }
      }

      const res = await AuthService.getQuestionnaireResponses({});
      // API may return { data: [...], total: N } or a plain array
      const items = Array.isArray(res) ? res : (res as any)?.data ?? [];

      // Fetch patient details in parallel for items missing name/gender
      const missingPatientIds: string[] = [
        ...new Set<string>(
          items
            .filter((item: any) => !item.first_name && item.patient_id)
            .map((item: any) => item.patient_id as string)
        ),
      ];

      const patientCache = new Map<string, any>();
      await Promise.all(
        missingPatientIds.map(async (pid) => {
          try {
            const p = await AuthService.getPatient(pid);
            if (p) patientCache.set(pid, p);
          } catch {
            // silently skip
          }
        })
      );

      const transformed = items.map((item: any) => {
        const p = patientCache.get(item.patient_id) ?? {};
        const firstName = item.first_name || p.first_name || "";
        const middleName = item.middle_name || p.middle_name || "";
        const lastName = item.last_name || p.last_name || "";
        const gender = item.gender || p.gender || "";
        const dob = item.date_of_birth || p.date_of_birth || "";
        const mobile = item.mobile_number || p.mobile_number || "No Number";

        return {
          id: item.id,
          questionnaire_type: item.questionnaire_type,
          completed_at: item.created_at?.split("T")[0] ?? "-",
          created_at: item.created_at,
          total_score: item.total_score,
          status: item.status,
          sms_sent_at: item.sms_sent_at,
          answers: item.answers,
          patient_id: item.patient_id,
          patient_name: [firstName, middleName, lastName].filter(Boolean).join(" ").trim() || item.patient_id,
          gender,
          date_of_birth: formatDOB(dob),
          age: calculateAge(dob),
          mobile_number: mobile,
          patient: {
            id: item.patient_id,
            first_name: firstName,
            middle_name: middleName,
            last_name: lastName,
            gender,
            date_of_birth: dob,
            mobile_number: mobile,
          },
        };
      });

      setRawData(transformed);
    } catch (error: any) {
      console.error("Fetch error:", error.message);
    }
  };

  useEffect(() => {
    (async () => {
      await fetchData();
      await fetchDoctors();
    })();
  }, []);

  useEffect(() => {
    if (selectedEmailResponses.length > 0 && allDoctors.length > 0) {
      const selectedPatientIds = [...new Set(selectedEmailResponses.map(r => String(r.patient_id)))];
      fetchRelevantDoctors(selectedPatientIds);
    } else if (selectedEmailResponses.length === 0) {
      setRelevantDoctors([]);
      setPatientDoctorMap(new Map());
    }
  }, [selectedEmailResponses, allDoctors]);

  useEffect(() => {
    const filtered = rawData.filter((item) => {
      // CMS Alert filter
      if (showAlertOnly && !item.highest_alert_severity) return false;
      // Role-based filtering for Doctors
      if (isDoctor && !assignedPatientIds.includes(String(item.patient_id))) {
        return false;
      }

      const status = String(item.status || 'pending for review').toLowerCase().trim();

      if (item.total_score === null) return false;
      const isHighScore = item.total_score > 15;
      if (isHighScore && !showRed) return false;
      if (!isHighScore && !showBlue) return false;

      const isPending = !status || status === 'pending for review';
      const isCompleted = status.includes('complete') || status === 'submitted' || status === 'completed';
      const isReviewed = status.includes('review') || status === 'reviewed';

      const shouldShow = (isPending && showPending) ||
        (isCompleted && showCompleted) ||
        (isReviewed && showReviewed);

      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch =
          (item.patient_name?.toLowerCase().includes(searchLower) || false) ||
          (item.questionnaire_type?.toLowerCase().includes(searchLower) || false) ||
          (item.mobile_number?.includes(searchTerm) || false) ||
          (item.completed_at?.includes(searchTerm) || false) ||
          status.includes(searchTerm.toLowerCase());
        if (!matchesSearch) return false;
      }

      return shouldShow;
    });

    filtered.sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });

    setData(filtered);
  }, [rawData, showRed, showBlue, showPending, showCompleted, showReviewed, searchTerm, sortOrder, isDoctor, assignedPatientIds]);

  const handleEmailSelect = (row: QuestionnaireResponseTypes, checked: boolean) => {
    setSelectedEmailResponses(prev =>
      checked ? [...prev, row] : prev.filter(item => item.id !== row.id)
    );
  };

  const handleSelectAllEmail = (checked: boolean) => {
    setSelectAllEmail(checked);
    setSelectedEmailResponses(checked ? data : []);
  };

  const handleEmailModalClose = async () => {
    setEmailModalOpen(false);
    setSelectedEmailResponses([]);
    setSelectAllEmail(false);
    await fetchData();
  };

  const handleBulkResendSMS = async () => {
    if (selectedEmailResponses.length === 0) return;

    if (!window.confirm(`Are you sure you want to invalidate ${selectedEmailResponses.length} selected response(s) and send new SMS links?`)) {
      return;
    }

    const progressToast = toast.loading(`Processing ${selectedEmailResponses.length} SMS resends...`);
    let successCount = 0;

    try {
      for (const response of selectedEmailResponses) {
        // 1. Invalidate current response
        await AuthService.updateQuestionnaireResponse(String(response.id), {
          status: "Invalidated",
          is_it_valid_response: 0
        });

        // 2. Generate new unique URL
        const patientId = String(response.patient_id);
        const uniqueUrl = await generateSMSEmbedUrlForPatient(patientId);

        if (!uniqueUrl) {
          console.error(`Failed to generate SMS URL for patient ${patientId}`);
          continue;
        }

        // 3. Send SMS
        const patientName = response.patient_name || "Patient";
        const firstName = patientName.split(" ")[0] || "there";
        const clinicPhone = formatClinicPhone((response as any).clinic_phone || (response as any).phone || "(555) 000-0004");
        const messageBody = `Ally Psychiatry (Pelham) via MonitorHealth: Hi ${firstName}, your care team has sent you a health questionnaire to complete: ${uniqueUrl}\nThis link expires in 48 hours. Questions? Call ${clinicPhone}. Text STOP to opt out.`;

        const { success } = await sendBulkSMS([{
          to: response.mobile_number,
          body: messageBody,
          name: patientName
        }]);

        if (success) {
          // 4. Create SMS log
          const cognitoUser = await AuthService.getCurrentUser();
          const currentStaffProfile = cognitoUser?.username ? await AuthService.getProfile(cognitoUser.username) : null;

          await AuthService.createSMSLog([{
            patient_id: patientId,
            sent_times: 1,
            creator_id: currentStaffProfile?.id || null,
            unique_page_id: uniqueUrl.split("/").pop(),
            message_body: messageBody,
            status: "sent",
          }]);
          successCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully resent ${successCount} SMS message(s)!`, { id: progressToast });
        setSelectedEmailResponses([]);
        setSelectAllEmail(false);
        await fetchData();
      } else {
        toast.error("Failed to resend any SMS messages.", { id: progressToast });
      }
    } catch (error) {
      console.error("Bulk resend SMS error:", error);
      toast.error("An error occurred during bulk SMS resend.", { id: progressToast });
    }
  };

  const groupedPatients = selectedEmailResponses.reduce((acc, response) => {
    const patientId = String(response.patient_id);
    if (!acc[patientId]) {
      acc[patientId] = {
        patient: response,
        responses: [],
        doctors: patientDoctorMap.get(patientId) || []
      };
    }
    acc[patientId].responses.push(response);
    return acc;
  }, {} as Record<string, { patient: QuestionnaireResponseTypes; responses: QuestionnaireResponseTypes[]; doctors: any[] }>);

  const columns: ColumnDef<QuestionnaireResponseTypes>[] = [
    {
      id: "select-email",
      header: () => (
        <div className="flex flex-col items-center">
          <span className="text-xs mb-1"></span>
          <Checkbox
            className="border border-black"
            checked={selectAllEmail}
            onCheckedChange={handleSelectAllEmail}
            aria-label="Select all for email"
          />
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex justify-center">
          <Checkbox
            className="border border-black"
            checked={selectedEmailResponses.some(item => item.id === row.original.id)}
            onCheckedChange={(value) => handleEmailSelect(row.original, !!value)}
            aria-label="Select row for email"
          />
        </div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "patient_name",
      header: "Patient Name",
      cell: ({ row }) => (
        <a
          href={`/patients/${row.original.patient_id}`}
          className="font-bold text-[#1a7a4a] hover:text-[#15633c] transition-colors"
          onClick={(e) => {
            e.preventDefault();
            navigate(`/patients/${row.original.patient_id}`);
          }}
        >
          {row.original.patient_name}
        </a>
      ),
    },
    {
      accessorKey: "gender",
      header: "Gender/Age",
      cell: ({ row }) => (
        <span>{`${getGenderInitial(row.original.gender)}/${row.original.age}`}</span>
      ),
    },
    {
      accessorKey: "questionnaire_type",
      header: "Questionnaire Type",
      cell: ({ row }) => <span>{row.original.questionnaire_type}</span>,
    },
    {
      accessorKey: "total_score",
      header: "Total Score",
      cell: ({ row }) => {
        const score = row.original.total_score;
        const scoreStyle = score !== null && score > 15
          ? "text-red-500 font-bold"
          : "text-blue-500 font-bold";
        return <span className={scoreStyle}>{score !== null && score > 0 ? score : "—"}</span>;
      },
    },
    {
      accessorKey: "highest_alert_severity",
      header: "CMS Alert",
      cell: ({ row }) => {
        const sev = row.original.highest_alert_severity;
        if (!sev) return <span className="text-gray-300 text-xs">—</span>;
        const styles: Record<string, string> = {
          critical: "bg-red-50 text-red-700 border-red-200",
          warning: "bg-amber-50 text-amber-700 border-amber-200",
          info: "bg-blue-50 text-blue-700 border-blue-200",
        };
        return (
          <div className={cn(
            "inline-flex items-center px-3 py-1 rounded-full text-xs font-bold border uppercase tracking-wider",
            styles[sev] || "bg-gray-100 text-gray-600 border-gray-200"
          )}>
            {sev}
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      header: "Review Status",
      cell: ({ row }) => {
        const status = row.original.status?.toLowerCase() || "";
        let statusStyles = "bg-gray-100 text-gray-800 border-gray-200";
        if (status.includes("submitted for review")) {
          statusStyles = "bg-emerald-50 text-emerald-700 border-emerald-100";
        } else if (status.includes("pending for review")) {
          statusStyles = "bg-amber-50 text-amber-700 border-amber-100";
        } else if (status.includes("reviewed by doctor")) {
          statusStyles = "bg-blue-50 text-blue-700 border-blue-100";
        }
        return (
          <div
            className={cn(
              "inline-flex items-center px-3 py-1 rounded-full text-xs font-bold border uppercase tracking-wider",
              statusStyles
            )}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </div>
        );
      },
    },
    {
      accessorKey: "created_at",
      header: "Submitted On",
      cell: ({ row }) => (
        <span>{formatDate(row.original.created_at, "MM/dd/yyyy")}</span>
      ),
    },
  ];

  const table = useReactTable({ data, columns, getCoreRowModel: getCoreRowModel() });

  return (
    <MainLayout>
      <div className="min-h-screen bg-[#f7f8fa] p-6 lg:p-10 font-sans">
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div>
              <h1 className="text-3xl lg:text-4xl font-extrabold text-[#1a7a4a] tracking-tight">
                Questionnaire Responses
              </h1>
              <p className="text-gray-500 mt-2 text-lg">
                Monitor and review patient screening results
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <div className="relative group">
                <input
                  type="text"
                  placeholder="Search responses..."
                  className="pl-12 pr-4 h-12 w-64 lg:w-80 bg-white border-gray-200 focus:border-[#1a7a4a] focus:ring-[#1a7a4a] rounded-2xl text-sm transition-all shadow-sm group-hover:shadow-md"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <svg
                  className="absolute left-4 top-3.5 h-5 w-5 text-gray-400 group-hover:text-[#1a7a4a] transition-colors"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>

              <div className="flex items-center bg-white p-1 rounded-2xl border border-gray-100 shadow-sm">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSortOrder("desc")}
                  className={`h-10 px-4 rounded-xl text-xs font-bold transition-all ${sortOrder === "desc"
                    ? "bg-[#1a7a4a] text-white shadow-md"
                    : "text-gray-500 hover:bg-gray-50"
                    }`}
                >
                  Newest
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSortOrder("asc")}
                  className={`h-10 px-4 rounded-xl text-xs font-bold transition-all ${sortOrder === "asc"
                    ? "bg-[#1a7a4a] text-white shadow-md"
                    : "text-gray-500 hover:bg-gray-50"
                    }`}
                >
                  Oldest
                </Button>
              </div>
            </div>
          </div>

          {/* Filters and Actions */}
          <Card className="rounded-3xl border-gray-100 shadow-xl shadow-gray-200/50 bg-white overflow-visible">
            <CardContent className="p-6">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
                  {/* Score Filters */}
                  <div className="flex items-center gap-6">
                    <div className="flex items-center space-x-3 group cursor-pointer">
                      <Checkbox
                        id="blue-score"
                        checked={showBlue}
                        onCheckedChange={(v) => setShowBlue(Boolean(v))}
                        className="w-5 h-5 border-2 border-blue-200 data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500 rounded-lg transition-all"
                      />
                      <label
                        htmlFor="blue-score"
                        className="text-sm font-bold text-blue-600 cursor-pointer select-none"
                      >
                        Score ≤ 15
                      </label>
                    </div>
                    <div className="flex items-center space-x-3 group cursor-pointer">
                      <Checkbox
                        id="red-score"
                        checked={showRed}
                        onCheckedChange={(v) => setShowRed(Boolean(v))}
                        className="w-5 h-5 border-2 border-red-200 data-[state=checked]:bg-red-500 data-[state=checked]:border-red-500 rounded-lg transition-all"
                      />
                      <label
                        htmlFor="red-score"
                        className="text-sm font-bold text-red-600 cursor-pointer select-none"
                      >
                        Score {" > "} 15
                      </label>
                    </div>
                  </div>

                  <div className="w-px h-8 bg-gray-100 hidden lg:block" />

                  {/* CMS Alert Filter */}
                  <div className="flex items-center space-x-3 group cursor-pointer">
                    <Checkbox
                      id="alert-filter"
                      checked={showAlertOnly}
                      onCheckedChange={(v) => setShowAlertOnly(Boolean(v))}
                      className="w-5 h-5 border-2 border-red-200 data-[state=checked]:bg-red-500 data-[state=checked]:border-red-500 rounded-lg transition-all"
                    />
                    <label
                      htmlFor="alert-filter"
                      className="text-sm font-bold text-red-600 cursor-pointer select-none"
                    >
                      CMS Alerts only
                    </label>
                  </div>

                  <div className="w-px h-8 bg-gray-100 hidden lg:block" />

                  {/* Status Filters */}
                  <div className="flex flex-wrap items-center gap-6">
                    <div className="flex items-center space-x-3 group cursor-pointer">
                      <Checkbox
                        id="pending-status"
                        checked={showPending}
                        onCheckedChange={(v) => setShowPending(Boolean(v))}
                        className="w-5 h-5 border-2 border-amber-200 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500 rounded-lg transition-all"
                      />
                      <label
                        htmlFor="pending-status"
                        className="text-sm font-bold text-amber-600 cursor-pointer select-none"
                      >
                        Pending
                      </label>
                    </div>
                    <div className="flex items-center space-x-3 group cursor-pointer">
                      <Checkbox
                        id="completed-status"
                        checked={showCompleted}
                        onCheckedChange={(v) => setShowCompleted(Boolean(v))}
                        className="w-5 h-5 border-2 border-emerald-200 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500 rounded-lg transition-all"
                      />
                      <label
                        htmlFor="completed-status"
                        className="text-sm font-bold text-emerald-600 cursor-pointer select-none"
                      >
                        Submitted
                      </label>
                    </div>
                    <div className="flex items-center space-x-3 group cursor-pointer">
                      <Checkbox
                        id="reviewed-status"
                        checked={showReviewed}
                        onCheckedChange={(v) => setShowReviewed(Boolean(v))}
                        className="w-5 h-5 border-2 border-blue-200 data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500 rounded-lg transition-all"
                      />
                      <label
                        htmlFor="reviewed-status"
                        className="text-sm font-bold text-blue-600 cursor-pointer select-none"
                      >
                        Reviewed
                      </label>
                    </div>
                  </div>
                </div>

                {/* Email Actions */}
                <div>
                  {selectedEmailResponses.length > 0 ? (
                    <div className="flex flex-col items-end gap-3">
                      <div className="flex items-center gap-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowPatientDetails(!showPatientDetails)}
                          className="h-11 px-5 rounded-2xl border-gray-200 text-gray-600 hover:text-[#1a7a4a] hover:bg-gray-50 transition-all font-semibold flex items-center gap-2"
                        >
                          {showPatientDetails ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                          {Object.keys(groupedPatients).length} Patient(s)
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleBulkResendSMS}
                          className="h-11 px-6 rounded-2xl border-amber-200 bg-amber-50 hover:bg-amber-100 text-amber-700 shadow-md shadow-amber-100 transition-all hover:scale-[1.02] flex items-center gap-2 font-semibold"
                        >
                          <MessageSquare className="h-4 w-4" />
                          Resend SMS ({selectedEmailResponses.length})
                        </Button>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => setEmailModalOpen(true)}
                          className="h-11 px-6 rounded-2xl bg-[#1a7a4a] hover:bg-[#15633c] text-white shadow-lg shadow-green-100 transition-all hover:scale-[1.02] flex items-center gap-2 font-semibold"
                        >
                          <Mail className="h-4 w-4" />
                          Email to Doctors
                        </Button>
                      </div>
                      {showPatientDetails && (
                        <div className="absolute top-full right-0 mt-4 z-50 w-80 bg-white border border-gray-100 rounded-3xl shadow-2xl p-6 animate-in fade-in slide-in-from-top-4 duration-300">
                          <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <Users className="w-5 h-5 text-[#1a7a4a]" />
                            Selected Patients
                          </h4>
                          <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                            {Object.entries(groupedPatients).map(
                              ([patientId, data]) => (
                                <div key={patientId} className="group">
                                  <div className="flex items-center justify-between gap-3 p-3 rounded-2xl bg-gray-50 group-hover:bg-[#1a7a4a]/5 transition-colors">
                                    <div className="flex items-center gap-3">
                                      <div className="w-8 h-8 rounded-full bg-[#1a7a4a]/10 flex items-center justify-center text-[#1a7a4a] font-bold text-xs">
                                        {data.patient.patient_name?.[0]}
                                      </div>
                                      <div>
                                        <p className="font-bold text-sm text-gray-900 leading-tight">
                                          {data.patient.patient_name}
                                        </p>
                                        <p className="text-xs text-gray-500 font-medium">
                                          {data.responses.length} report(s)
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      disabled
                      className="h-11 px-6 rounded-2xl border-gray-200 text-gray-400 font-semibold flex items-center gap-2"
                    >
                      <Mail className="h-4 w-4" />
                      Select for Email
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Results Table */}
          <Card className="rounded-3xl border-gray-100 shadow-xl shadow-gray-200/50 bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50/50 border-b border-gray-100">
                    {table.getHeaderGroups().map((headerGroup) =>
                      headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          className="px-6 py-5 text-xs font-bold uppercase tracking-wider text-gray-500"
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </th>
                      ))
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {table.getRowModel().rows.map((row) => (
                    <tr
                      key={row.id}
                      className="group hover:bg-[#1a7a4a]/5 transition-colors"
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="px-6 py-4 whitespace-nowrap">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data.length === 0 && (
              <div className="p-20 text-center">
                <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Mail className="w-10 h-10 text-gray-300" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  No responses found
                </h3>
                <p className="text-gray-500">
                  Try adjusting your search or filters to find what you're looking for.
                </p>
              </div>
            )}
          </Card>
        </div>

        <EmailAction
          modalOpen={emailModalOpen}
          setModalOpen={handleEmailModalClose}
          selectedResponses={selectedEmailResponses}
          doctors={relevantDoctors.map(doctor => ({
            ...doctor,
            id: Number(doctor.id) || 0,
            name: doctor.name || '',
            email: doctor.email || ''
          }))}
          patientDoctorMap={patientDoctorMap}
          groupedPatients={groupedPatients}
          isFetchingDoctors={isFetchingDoctors}
        />
      </div>
    </MainLayout>
  );
}